import { useCallback } from 'react';
import { useShowToast } from '@/hooks';
import {
  useFillScoreCardWithAiMutation,
  usePollScoreCardWithAiMutation,
} from '@/store/query/interviews';

const MAX_RETRIES = 25;
const POLLING_INTERVAL = 3000;

export const useScoreCardAI = ({
  ats_application_id,
  scorecardId,
  interviewId,
  onSuccess,
}) => {
  const toast = useShowToast();
  const [fillWithAi, { isLoading: isFilling }] =
    useFillScoreCardWithAiMutation();
  const [pollAiResults, { isLoading: isPolling }] =
    usePollScoreCardWithAiMutation();

  const pollAiResultsHelper = useCallback(
    async (msg_id, retryCount = 0) => {
      if (retryCount >= MAX_RETRIES) {
        toast({
          title: 'AI processing timed out',
          description: 'Please try again later',
          status: 'error',
        });
        return;
      }

      try {
        const response = await pollAiResults({
          applicantId: ats_application_id,
          scorecardId,
          msg_id,
        }).unwrap();

        if (response?.status === 'success') {
          toast({
            title: 'AI filled the scorecard successfully',
            status: 'success',
          });
          onSuccess(response.scorecard);
        } else if (response?.status === 'queued') {
          setTimeout(
            () => pollAiResultsHelper(msg_id, retryCount + 1),
            POLLING_INTERVAL
          );
        } else if (response.status === 'failed') {
          toast({
            title: 'AI processing failed',
            description: response.error || 'An unexpected error occurred',
            status: 'error',
          });
          onSuccess(null, true);
        }
      } catch (error) {
        toast({
          title: 'Error polling AI results',
          description: error.message || 'An unexpected error occurred',
          status: 'error',
        });
        onSuccess(null, true);
      }
    },
    [ats_application_id, scorecardId, pollAiResults, toast, onSuccess]
  );

  const handleFillWithAi = async () => {
    const response = await fillWithAi({
      scorecardId,
      ats_interview_id: interviewId,
      applicantId: ats_application_id,
    });

    if ('error' in response) {
      toast({
        title: 'Error starting AI fill',
        description: response?.error?.[0],
        status: 'error',
      });
      return false;
    }

    const msg_id = response?.data?.msg_id;
    if (msg_id) {
      pollAiResultsHelper(msg_id);
      return true;
    }

    throw new Error('No message ID received from AI fill request');
  };

  return {
    handleFillWithAi,
    isFilling,
    isPolling,
  };
};
